import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import countryService from '../services/countryService';
import corporateProfileService from '../services/corporateProfileService';
import OhipLovPicker from './OhipAgentTypePicker';
import './AddCorporateProfilePage.css';

const AGENT_TYPES = [
    { value: 'OTA', label: 'OTA (Online Travel Agent)' },
    { value: 'COM', label: 'COM (Company)' },
    { value: 'SRC', label: 'SRC (Source)' },
    { value: 'TRA', label: 'TRA (Travel Agent)' },
];

const EditCorporateProfilePage = () => {
    const navigate = useNavigate();
    const { ppId, profileId } = useParams();
    const [searchParams] = useSearchParams();
    const ruleId = searchParams.get('ruleId');
    const backUrl = ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}` : `/apps/payment-porter/${ppId}`;
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [countries, setCountries] = useState([]);

    useEffect(() => {
        countryService.getAllCountries().then(res => {
            setCountries(res.data?.countries || []);
        }).catch(() => {});

        corporateProfileService.getById(ppId, profileId).then(res => {
            const p = res.data?.profile;
            if (p) {
                setForm({
                    agentType: p.agent_type || 'COM',
                    profileId: p.profile_id || '',
                    name:      p.name       || '',
                    address:   p.address    || '',
                    city:      p.city       || '',
                    country:   p.country    || '',
                });
            }
        }).catch(() => setErrors({ submit: 'Failed to load profile' }));
    }, [ppId, profileId]);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.agentType) e.agentType = 'Agent Type is required';
        if (!form.profileId.trim()) e.profileId = 'Profile ID is required';
        if (!form.name.trim()) e.name = 'Name is required';
        return e;
    };

    const handleSave = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await corporateProfileService.update(ppId, profileId, {
                agent_type: form.agentType,
                profile_id: form.profileId,
                name:       form.name,
                address:    form.address || null,
                city:       form.city || null,
                country:    form.country || null,
            });
            navigate(backUrl);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save profile' });
        } finally {
            setSaving(false);
        }
    };

    if (!form) return <div className="acp-page"><div className="acp-card" style={{ color: '#6b7280' }}>Loading...</div></div>;

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">Edit Corporate Profile</h1>
                    <p className="acp-subtitle">Update Opera Cloud Corporate Profile ID configuration</p>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Agent Type <span className="acp-required">*</span></label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                <select
                                    className={`acp-select${errors.agentType ? ' acp-input-error' : ''}`}
                                    value={form.agentType}
                                    onChange={e => set('agentType', e.target.value)}
                                >
                                    {AGENT_TYPES.map(t => (
                                        <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                    {!AGENT_TYPES.find(t => t.value === form.agentType) && form.agentType && (
                                        <option value={form.agentType}>{form.agentType}</option>
                                    )}
                                </select>
                                <OhipLovPicker
                                    fetchFn={() => corporateProfileService.getOhipAgentTypes(ppId)}
                                    title="Select Agent Type from OHIP"
                                    emptyMsg="No agent types returned from OHIP."
                                    onSelect={code => set('agentType', code)}
                                />
                            </div>
                            {errors.agentType && <span className="acp-error-msg">{errors.agentType}</span>}
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Opera Cloud Profile ID <span className="acp-required">*</span></label>
                            <input
                                className={`acp-input${errors.profileId ? ' acp-input-error' : ''}`}
                                type="text"
                                placeholder="e.g., 12345"
                                value={form.profileId}
                                onChange={e => set('profileId', e.target.value)}
                                autoFocus
                            />
                            {errors.profileId && <span className="acp-error-msg">{errors.profileId}</span>}
                        </div>
                    </div>

                    <div className="acp-field">
                        <label className="acp-label">Name <span className="acp-required">*</span></label>
                        <input
                            className={`acp-input${errors.name ? ' acp-input-error' : ''}`}
                            type="text"
                            placeholder="e.g., Booking.com"
                            value={form.name}
                            onChange={e => set('name', e.target.value)}
                        />
                        {errors.name && <span className="acp-error-msg">{errors.name}</span>}
                    </div>

                    <div className="acp-field">
                        <label className="acp-label">Address</label>
                        <input
                            className="acp-input"
                            type="text"
                            placeholder="Enter address"
                            value={form.address}
                            onChange={e => set('address', e.target.value)}
                        />
                    </div>

                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">City</label>
                            <input
                                className="acp-input"
                                type="text"
                                placeholder="Enter city"
                                value={form.city}
                                onChange={e => set('city', e.target.value)}
                            />
                        </div>
                        <div className="acp-field">
                            <label className="acp-label">Country</label>
                            <select
                                className="acp-select"
                                value={form.country}
                                onChange={e => set('country', e.target.value)}
                            >
                                <option value="">Select country</option>
                                {countries.map(c => (
                                    <option key={c.code} value={c.code}>{c.description}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button
                        className="acp-btn-cancel"
                        onClick={() => navigate(backUrl)}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="acp-btn-create"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditCorporateProfilePage;
